import React, { useState, useEffect, useRef } from 'react'
import WorkBenchFilters from '../Components/WorkBenchFilters'
import ServiceRecordsTable from '../Components/ServiceRecordsTable'
import CaregiverDetailsTable from '../Components/CaregiverDetailsTable'
import { RefreshCcwIcon } from 'lucide-react'
import { useOktaAuth } from '@okta/okta-react'
import CssApiClient from '../Components/Api'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import toast, { Toaster } from 'react-hot-toast'
import { useUser } from '../Context/UserContext'

function WorkbenchPage () {
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedCss, setSelectedCss] = useState([])
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedServiceRecord, setSelectedServiceRecord] = useState(null)
  const [caregiverDetails, setCaregiverDetails] = useState([])
  const { oktaAuth, authState } = useOktaAuth()
  const userInfo = useUser()

  const [serviceRecords, setServiceRecords] = useState([])
  const [isServiceInitialLoading, setIsServiceInitialLoading] = useState(false)
  const [isServiceLoadingMore, setIsServiceLoadingMore] = useState(false)
  const [serviceBatchNumber, setServiceBatchNumber] = useState(1)
  const [hasMoreServiceRecords, setHasMoreServiceRecords] = useState(true)
  const [filtersInitialized, setFiltersInitialized] = useState(false)

  const [isCaregiverInitialLoading, setIsCaregiverInitialLoading] =
    useState(false)
  const [isCaregiverLoadingMore, setIsCaregiverLoadingMore] = useState(false)
  const [caregiverBatchNumber, setCaregiverBatchNumber] = useState(1)
  const [hasMoreCaregivers, setHasMoreCaregivers] = useState(true)

  const batchSize = 500
  const navigate = useNavigate()
  const abortControllerRef = useRef(null)
  const requestIdRef = useRef(0)

  const caregiverAbortRef = useRef(null)
  const caregiverRequestIdRef = useRef(0)
  const [role, setRole] = useState([])

  const fetchInitialServiceRecords = async () => {
    if (!authState?.isAuthenticated) {
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    const currentRequestId = ++requestIdRef.current

    setIsServiceInitialLoading(true)
    setServiceBatchNumber(1)
    setHasMoreServiceRecords(true)

    try {
      const payload = {
        month: selectedMonth,
        css: selectedCss.map(c => c.value),
        status: selectedStatus,
        batch_number: 1,
        batch_size: batchSize
      }

      const data = await CssApiClient.postCSSData(oktaAuth, payload, {
        signal: controller.signal
      })

      if (currentRequestId === requestIdRef.current) {
        const newRecords = data?.data || []
        setServiceRecords(newRecords)
        setServiceBatchNumber(prev => prev + 1)
        if (newRecords.length < batchSize) {
          setHasMoreServiceRecords(false)
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Request cancelled because a new one started')
      } else {
        console.error('Failed to load service records', err)
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsServiceInitialLoading(false)
      }
    }
  }

  const fetchMoreServiceRecords = async () => {
    if (
      !authState?.isAuthenticated ||
      !hasMoreServiceRecords ||
      isServiceLoadingMore
    ) {
      return
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    const currentRequestId = ++requestIdRef.current
    setIsServiceLoadingMore(true)
    try {
      const payload = {
        month: selectedMonth,
        css: selectedCss.map(c => c.value),
        status: selectedStatus,
        batch_number: serviceBatchNumber,
        batch_size: batchSize
      }

      const data = await CssApiClient.postCSSData(oktaAuth, payload, {
        signal: controller.signal
      })

      if (currentRequestId === requestIdRef.current) {
        const newRecords = data?.data || []
        setServiceRecords(prev => [...prev, ...newRecords])
        setServiceBatchNumber(prev => prev + 1)

        if (newRecords.length < batchSize) {
          setHasMoreServiceRecords(false)
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Request cancelled because a new one started')
      } else {
        console.error('Failed to load service records', err)
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsServiceLoadingMore(false)
      }
    }
  }

  const fetchCaregiverDetails = async () => {
    if (!authState?.isAuthenticated || !hasMoreCaregivers) return

    if (selectedServiceRecord?.CLIENT_NUMBER === undefined) {
      setCaregiverDetails([])
      setCaregiverBatchNumber(1)
      setSelectedServiceRecord(null)
      return
    }
    if (caregiverAbortRef.current) {
      caregiverAbortRef.current.abort()
    }

    const controller = new AbortController()
    caregiverAbortRef.current = controller
    const currentRequestId = ++caregiverRequestIdRef.current

    const isInitial = caregiverBatchNumber === 1
    if (isInitial) {
      setIsCaregiverInitialLoading(true)
    } else {
      setIsCaregiverLoadingMore(true)
    }

    try {
      const payload = {
        client_number: selectedServiceRecord?.CLIENT_NUMBER,
        state: selectedServiceRecord?.BRANCH_STATE_CODE,
        batch_number: caregiverBatchNumber,
        batch_size: batchSize
      }

      const data = await CssApiClient.postCaregivers(oktaAuth, payload, {
        signal: controller.signal
      })

      if (currentRequestId === caregiverRequestIdRef.current) {
        const newCaregiverDetails = data?.data || []
        setCaregiverDetails(prev =>
          isInitial ? newCaregiverDetails : [...prev, ...newCaregiverDetails]
        )
        setCaregiverBatchNumber(prev => prev + 1)
        if (newCaregiverDetails.length < batchSize) {
          setHasMoreCaregivers(false)
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Caregiver request cancelled because a new one started')
      } else {
        console.error('Failed to load caregiver details', err)
      }
    } finally {
      if (currentRequestId === caregiverRequestIdRef.current) {
        setIsCaregiverInitialLoading(false)
        setIsCaregiverLoadingMore(false)
      }
    }
  }

  const handleExport = async sortedRecords => {
    const toastId = toast.loading('Preparing export...')
    if (!hasMoreServiceRecords || role.includes(userInfo?.job_profile)) {
      try {
        const formatted = sortedRecords.map(rec => ({
          CSS: rec.CSS_NAME,
          'Client Number': rec.CLIENT_NUMBER,
          'Client Name': rec.CLIENT_NAME,
          'Contract Name': rec.CONTRACT_NAME,
          'Service Name': rec.SERVICE_NAME,
          'Future Hrs/Visits No Caregiver':
            rec.FUTURE_HOURS_NO_CAREGIVER != null &&
            !isNaN(rec.FUTURE_HOURS_NO_CAREGIVER)
              ? Number(rec.FUTURE_HOURS_NO_CAREGIVER).toFixed(2)
              : '-',
          'Potential Under Served':
            rec.POTENTIAL_UNDER_SERVED != null &&
            !isNaN(rec.POTENTIAL_UNDER_SERVED)
              ? Number(rec.POTENTIAL_UNDER_SERVED).toFixed(2)
              : '-'
        }))

        const csv = Papa.unparse(formatted)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'CSS_DATA.csv'
        a.click()
        URL.revokeObjectURL(url)
        toast.success('Export completed!', { id: toastId })
      } catch (err) {
        toast.error('Something went wrong', { id: toastId })
      }
    } else {
      try {
        const filters = {
          MONTH_YEAR: selectedMonth,
          SERVICE_STATUS: selectedStatus
        }
        if (selectedCss?.length) {
          const cssfilter = selectedCss.reduce(
            (acc, { state = 'UNKNOWN', value }) => {
              ;(acc[state] ||= []).push(value)
              return acc
            },
            {}
          )
          filters.CSS_NAME = cssfilter
        }

        const { messageId } = await CssApiClient.fileDownloadRequest(
          oktaAuth,
          filters
        )
        let status = 'PENDING'
        let url = null
        
        const startTime = Date.now()
        const timeoutMs = 7 * 60 * 1000 // 7 minutes

        while (status === 'PENDING') {
          if (Date.now() - startTime > timeoutMs) {
            toast.error('Export timed out. Please try again.', {
              id: toastId
            })
            return
          }
          
          await new Promise(r => setTimeout(r, 7000))
          const res = await CssApiClient.fileDownloadStatus(oktaAuth, {
            messageId
          })
          status = res.status
          url = res.pre_signed_url
        }

        if (status === 'SUCCEEDED' && url) {
          const a = document.createElement('a')
          a.href = url
          a.click()
          toast.success('Export completed!', { id: toastId })
        } else {
          toast.error('Export failed. Please try again.', { id: toastId })
        }
      } catch (err) {
        toast.error('Something went wrong', { id: toastId })
      }
    }
  }

  useEffect(() => {
    if (filtersInitialized) {
      fetchInitialServiceRecords()
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [filtersInitialized, selectedMonth, selectedCss, selectedStatus])

  return (
    <main className='flex-1 overflow-hidden'>
      <div className='h-full p-6 overflow-y-auto'>
        <div className='container m-auto mb-3 flex'>
          <h2 className='flex-1 text-2xl font-bold text-gray-900 text-center'>
            CSS Workbench
          </h2>
          <button
            className='flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg'
            onClick={() => {
              navigate('/')
              window.location.reload()
            }}
          >
            <RefreshCcwIcon className='w-4 h-4' />
            <span>Refresh</span>
          </button>
        </div>

        <WorkBenchFilters
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedCss={selectedCss}
          setSelectedCss={setSelectedCss}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          setBatchNumber={setServiceBatchNumber}
          setHasMoreServiceRecords={setHasMoreServiceRecords}
          setFiltersInitialized={setFiltersInitialized}
          setCaregiverDetails={setCaregiverDetails}
          setCaregiverBatchNumber={setCaregiverBatchNumber}
          setSelectedRecord={setSelectedServiceRecord}
          role={role}
          setRole={setRole}
        />

        <ServiceRecordsTable
          records={serviceRecords}
          isInitialLoading={isServiceInitialLoading}
          isLoadingMore={isServiceLoadingMore}
          selectedRecord={selectedServiceRecord}
          setSelectedRecord={setSelectedServiceRecord}
          fetchNextBatch={fetchMoreServiceRecords}
          hasMore={hasMoreServiceRecords}
          setCaregiverBatchNumber={setCaregiverBatchNumber}
          fetchCaregiverDetails={fetchCaregiverDetails}
          setHasMoreCaregivers={setHasMoreCaregivers}
          handleExport={handleExport}
        />

        <CaregiverDetailsTable
          selectedServiceRecord={selectedServiceRecord}
          caregiverDetails={caregiverDetails}
          isInitialLoading={isCaregiverInitialLoading}
          isLoadingMore={isCaregiverLoadingMore}
          fetchNextBatch={fetchCaregiverDetails}
          hasMore={hasMoreCaregivers}
        />
      </div>
    </main>
  )
}

export default WorkbenchPage
